// Keeps the location a signed-out visitor originally asked for (typically a
// shared deep link) across the forced trip through the login page, so that
// after signing in they land on that page instead of the dashboard home.
//
// sessionStorage rather than the more common `?redirect=` query parameter,
// because auth transitions here are full page loads (`window.location`) and the
// SAML/OIDC flows leave the app entirely and come back on their own callback
// URL — a query parameter would not survive that, and neither would React
// Router's `location.state`. sessionStorage is per tab, survives those
// navigations, and covers every login variant with one mechanism.
// `clearStorageWithExceptions()` preserves this key so the target also survives
// the storage wipe on logout — needed for the invited-user flow, where a forced
// password change logs the user out between their first and second login.

import {
  AUTHENTICATION_PATH_PREFIX,
  LOGIN_PATH,
  hasPathPrefix,
  isAuthenticationPath,
} from './authenticationPaths';

export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

/**
 * A tab can sit on the login page indefinitely, and the next person to sign in
 * there may not be the one whose page was remembered (shared machine, cross-tab
 * logout). Expiring the target keeps a login shortly after the interruption —
 * including the invited-user detour through the forced password change — while
 * an unrelated login hours later starts clean on the dashboard home.
 */
const POST_LOGIN_REDIRECT_MAX_AGE_MS = 30 * 60 * 1000;

/**
 * Never restored after login: the authentication pages themselves would loop
 * back into login, and `/demo` is a mode switch that asks a signed-in user
 * whether to wipe their session into demo mode — not a page to land on.
 */
const NON_RESTORABLE_PATH_PREFIXES = [AUTHENTICATION_PATH_PREFIX, '/demo'];

// Storage can be unavailable (blocked cookies, hardened privacy modes) and
// throws on access there. Losing the remembered target is acceptable; breaking
// the redirect to login it sits in front of is not.

interface StoredTarget {
  target: string;
  storedAt: number;
}

function readStoredTarget(): StoredTarget | null {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);

    if (!raw) return null;

    const entry: unknown = JSON.parse(raw);

    if (
      typeof entry !== 'object' ||
      entry === null ||
      typeof (entry as StoredTarget).target !== 'string' ||
      typeof (entry as StoredTarget).storedAt !== 'number'
    ) {
      return null;
    }

    return entry as StoredTarget;
  } catch {
    return null;
  }
}

function writeStoredTarget(target: string): void {
  try {
    const entry: StoredTarget = { target, storedAt: Date.now() };

    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, JSON.stringify(entry));
  } catch {
    /* empty */
  }
}

/**
 * Normalises the target, or returns undefined when it is not a location worth
 * restoring:
 *
 * - resolved against the current origin and required to stay on it, so no
 *   stored value can send the user off-site — `//evil.test`, `https://evil.test`
 *   and the separators browsers normalise into slashes (backslashes, stripped
 *   tabs and newlines) all resolve to a foreign origin and are rejected
 * - not the bare home page, which is where login lands anyway — but a home-page
 *   URL that carries state in its query string or hash is kept
 * - not one of the paths above
 */
function sanitizeTarget(target: string | null | undefined): string | undefined {
  if (!target?.startsWith('/')) return undefined;

  let url;

  try {
    url = new URL(target, window.location.origin);
  } catch {
    return undefined;
  }

  if (url.origin !== window.location.origin) return undefined;
  if (url.pathname === '/' && !url.search && !url.hash) return undefined;
  if (
    NON_RESTORABLE_PATH_PREFIXES.some((prefix) =>
      hasPathPrefix(url.pathname, prefix),
    )
  ) {
    return undefined;
  }

  return url.pathname + url.search + url.hash;
}

/**
 * Stores where to return to after login. Defaults to the current location
 * including query string and hash, since deep links carry their state there.
 * Call it immediately before navigating to login, never in an effect: the
 * browser may leave the page before a passive effect runs.
 */
export function rememberPostLoginRedirect(
  target: string = window.location.pathname +
    window.location.search +
    window.location.hash,
): void {
  const safeTarget = sanitizeTarget(target);

  if (!safeTarget) return;

  writeStoredTarget(safeTarget);
}

/**
 * Remembers the current location, then leaves for the login page. The single
 * chokepoint for "this session is over, go sign in again" — new logout-into-
 * login paths should call this rather than pairing the two steps by hand,
 * so none of them can forget the remembering half.
 *
 * Deliberate exceptions that navigate to login WITHOUT remembering: explicit
 * logout (`useLogoutFn`) and corrupted-storage recovery (`global-store.ts`),
 * where returning to the previous page is not wanted.
 */
export function redirectToLogin(): void {
  rememberPostLoginRedirect();
  window.location.replace(LOGIN_PATH);
}

/**
 * The pending post-login target, if any. Reading does not consume it: the value
 * is read while rendering a redirect, which React may run more than once —
 * `clearPostLoginRedirect()` drops it once the user has actually landed.
 */
export function readPostLoginRedirect(): string | undefined {
  const entry = readStoredTarget();

  if (!entry) return undefined;
  if (Date.now() - entry.storedAt > POST_LOGIN_REDIRECT_MAX_AGE_MS) {
    return undefined;
  }

  return sanitizeTarget(entry.target);
}

export function clearPostLoginRedirect(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    /* empty */
  }
}

/**
 * Which location a guard-requested redirect should actually go to. A signed-in
 * user being sent off an authentication page is the one case where a remembered
 * deep link wins over the guard's home-page fallback; every other redirect
 * (missing access, restricted module, demo mode) passes through untouched.
 *
 * A guard redirect AWAY from an authentication path implies a signed-in user:
 * the authentication routes are all guest or public, so they never redirect a
 * signed-out visitor. And demo mode cannot carry a pending target — it is only
 * ever entered signed-in via `/demo`, where the landing-page effect in
 * BaseRoute has already cleared any leftover. Hence no user or demo parameter.
 *
 * Pure by design — the caller supplies `pendingTarget` and performs the
 * navigation, so the decision stays testable without touching storage.
 */
export function resolvePostLoginTarget({
  redirectPath,
  pathname,
  pendingTarget,
}: {
  redirectPath: string | undefined;
  pathname: string;
  pendingTarget: string | undefined;
}): string | undefined {
  const restores = !!redirectPath && isAuthenticationPath(pathname);

  return restores ? (pendingTarget ?? redirectPath) : redirectPath;
}
